import * as vp from 'vopay';
import { useFormik } from 'formik';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { Prompt } from 'react-router-dom';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePendingData } from '@/providers/PendingData.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import type { PermissionsHandlers } from '../types';

const { Box, Button, SubTitle, Table, TextField, Toggle } = vp;

type PermissionsFormProps = {
  accountPermissions: vp.AccountPermissions;
  handlers: PermissionsHandlers;
  permissionRows: Record<string, vp.PartnerAccountPermissionResponse[]>;
};

type FC = FunctionComponent<PermissionsFormProps>;

const PermissionsForm: FC = ({ accountPermissions, handlers, permissionRows }) => {
  const { canUpdatePermissions } = useModuleFeatureFlag('accounts');
  const { initPendingData, updatePendingData, hasPendingData } = usePendingData();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // Get sorted section names using useMemo to prevent unnecessary recalculations
  const sectionNames = useMemo(() => Object.keys(permissionRows).sort((a, b) => a.localeCompare(b)), [permissionRows]);

  const formik = useFormik({
    initialValues: accountPermissions,
    onSubmit: handlers.onReview,
    enableReinitialize: true,
  });

  // Add a useEffect to detect permission changes and reset values
  useEffect(() => {
    // Force reset values whenever accountPermissions changes
    if (Object.keys(accountPermissions).length > 0) {
      formik.resetForm({ values: accountPermissions });

      // Also reset pending data to ensure it matches the new values
      initPendingData({ ...accountPermissions });
    }
  }, [accountPermissions]);

  // Initialize pending data once
  useEffect(() => {
    initPendingData(accountPermissions);
  }, []);

  // Update pending data when form values change
  useEffect(() => {
    if (Object.keys(formik.values).length > 0) {
      updatePendingData(formik.values as Record<string, string | boolean>);
    }
  }, [formik.values]);

  // Helper to format section names
  const formatSectionName = (section: string) =>
    section
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // Render field based on type
  const renderField = (permission: string, type: string, value: string | number | boolean) => {
    switch (type) {
      case 'boolean':
        return (
          <Toggle
            key={permission}
            name={permission}
            onBlur={formik.handleBlur}
            onChange={(_e, { isEnabled }) => {
              formik.setFieldValue(permission, isEnabled);
              handlers.onPermissionChange(permission, isEnabled);
            }}
            value={Boolean(value)}
            disabled={!canUpdatePermissions}
          />
        );
      case 'number':
        return (
          <TextField
            key={permission}
            name={permission}
            id={permission}
            variant="standard"
            preset="currency"
            onBlur={formik.handleBlur}
            onChange={(e, { value: maxValue }) => {
              formik.handleChange(e);
              handlers.onPermissionChange(permission, maxValue.replace(/,/g, ''));
            }}
            value={value === null || value === undefined ? '' : String(value)}
            disabled={!canUpdatePermissions}
          />
        );
      case 'string':
      case 'text':
        return (
          <TextField
            key={permission}
            name={permission}
            id={permission}
            variant="standard"
            preset={permission.toLowerCase().includes('amount') ? 'currency' : undefined}
            onBlur={formik.handleBlur}
            onChange={(e, { value: maxValue }) => {
              formik.handleChange(e);
              handlers.onPermissionChange(permission, maxValue.replace(/,/g, ''));
            }}
            value={String(value || '')}
            disabled={!canUpdatePermissions}
          />
        );
      default:
        return null;
    }
  };

  // Create table props for a section
  const getPermissionsTableProps = (sectionName: string): vp.TableProps => {
    const sectionPermissions = permissionRows[sectionName] || [];

    return {
      name: `account-permissions-${sectionName}`,
      variant: 'pair',
      columns: {
        permission: { header: '' },
        row: { header: '' },
      },
      rows: sectionPermissions.map(({ Label, Type, Permission }) => {
        // Skip if permission not found in values
        if (!(Permission in formik.values)) {
          return { permission: Label, row: null };
        }

        const fieldValue = formik.values[Permission];

        return {
          permission: Label,
          row: (
            <Box jc="fe" pt="sm" pb="sm">
              {renderField(Permission, Type, fieldValue)}
            </Box>
          ),
        };
      }),
    };
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box mt="lg">
        <SubTitle subTitle="Account Permissions" />
      </Box>

      {sectionNames.map((sectionName) => (
        <Box key={sectionName} mt="lg" flx="col">
          <Box pt="xmd" pb="smd" ps="sm">
            <SubTitle subTitle={formatSectionName(sectionName)} fontSize="16px" fontWeight="bold" />
          </Box>
          <Table {...getPermissionsTableProps(sectionName)} />
        </Box>
      ))}

      <Box mt="md" jc="fe">
        {canUpdatePermissions && (
          <Button id="accountPermissionsForm-submit" type="submit" label="Review" isDisabled={isRoleViewOnly} />
        )}
        <Prompt when={hasPendingData} message="You have unsaved account data that will be discarded." />
      </Box>
    </form>
  );
};

export default PermissionsForm;
